import React, { useEffect, useState } from 'react';
import { } from 'react-router-dom';
import { motion } from 'framer-motion';
import SkillGrid from './SkillGrid';
import SkillManagement from '../components/SkillManagement';
import { apiClient } from '../services/api';
import { useAuth } from '../context/AuthContext';
import AddSkillModal from '../components/AddSkillModal';
import TalentDiscoveryModal from '../components/talent/TalentDiscoveryModal';
import AppHeader from '../components/layout/AppHeader';
import SkillsTour from '../components/onboarding/SkillsTour';
import { FiPlus, FiCompass } from 'react-icons/fi';

const Skills = () => {
  // const navigate = useNavigate();
  const { user } = useAuth();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDiscoveryModalOpen, setIsDiscoveryModalOpen] = useState(false);
  const [userSkills, setUserSkills] = useState<any[]>([]);
  const [availableSkills, setAvailableSkills] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState<'grid' | 'list' | 'profile'>('grid');

  const fetchSkills = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      // Fetch user skills and available skills in parallel
      const [userData, allSkillsData] = await Promise.all([
        apiClient.getUserSkills(user.id),
        apiClient.getSkills()
      ]);

      // Handle user skills
      const skillsList = Array.isArray(userData) ? userData : (userData.skills || userData.data || []);
      const formattedSkills = skillsList.map((item: any) => ({
        id: item.skill?._id || item.skill?.id || item.id,
        name: item.skill?.name || item.name || 'Unknown',
        category: item.skill?.category || item.category,
        proficiency: item.proficiency || 1,
        endorsements: item.endorsements || 0,
        description: item.skill?.description,
        yearsOfExperience: item.yearsOfExperience,
        lastUsed: item.lastUsed,
        evidence: item.evidence // Preserve evidence for details view
      }));
      setUserSkills(formattedSkills);

      // Handle available skills
      const allSkills = Array.isArray(allSkillsData) ? allSkillsData : (allSkillsData.skills || []);
      setAvailableSkills(allSkills);
    } catch (err) {
      console.error('Failed to load skills:', err);
      setUserSkills([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchSkills();
    } else {
      setLoading(false);
    }
  }, [user]);

  return (
    <div className="min-h-screen bg-premium-wood-dark font-sans text-white overflow-x-hidden relative">
      <SkillsTour />
      {/* Wood Texture Background */}
      <div
        className="absolute inset-0 z-0 opacity-40 pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1543158143-4a1c0d0c3405?q=80&w=2670&auto=format&fit=crop')`,
          backgroundSize: 'cover',
          filter: 'contrast(1.2) brightness(0.8)'
        }}
      />
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-black/60 via-premium-wood-dark/80 to-black/90 pointer-events-none" />

      {/* Header */}
      <AppHeader />

      {/* Main Content */}
      <main className="relative z-10 container mx-auto px-4 pt-12 pb-24">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-7xl mx-auto">

          <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6">
            <div>
              <h1 id="skills-title" className="font-serif text-5xl md:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-b from-premium-gold-light via-premium-gold to-premium-gold-dark drop-shadow-lg mb-2">
                Skills & Talents
              </h1>
              <p className="text-lg text-white/60">Discover, visualize, and expand your capabilities.</p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                id="skills-discover"
                onClick={() => setIsDiscoveryModalOpen(true)}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white font-bold rounded-lg hover:shadow-[0_0_20px_rgba(59,130,246,0.5)] transition-all transform hover:scale-105 active:scale-95 flex items-center gap-2"
              >
                <FiCompass size={20} />
                Discover Talents
              </button>
              <button
                id="skills-add"
                onClick={() => setIsAddModalOpen(true)}
                className="px-6 py-3 bg-white/10 text-white border border-white/20 font-medium rounded-lg hover:bg-white/20 transition-all flex items-center gap-2"
              >
                <FiPlus size={20} />
                Quick Add
              </button>
            </div>
          </div>

          <div className="flex border-b border-white/10 mb-8" id="skills-tabs">
            <button
              id="skills-tab-3d"
              onClick={() => setView('grid')}
              className={`px-6 py-3 font-medium transition-colors relative ${view === 'grid' ? 'text-premium-gold' : 'text-white/60 hover:text-white'
                }`}
            >
              3D Universe
              {view === 'grid' && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-premium-gold"
                />
              )}
            </button>
            <button
              id="skills-tab-manage"
              onClick={() => setView('list')}
              className={`px-6 py-3 font-medium transition-colors relative ${view === 'list' ? 'text-premium-gold' : 'text-white/60 hover:text-white'
                }`}
            >
              Manage Skills
              {view === 'list' && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-premium-gold"
                />
              )}
            </button>
            <button
              id="skills-tab-profile"
              onClick={() => setView('profile')}
              className={`px-6 py-3 font-medium transition-colors relative ${view === 'profile' ? 'text-premium-gold' : 'text-white/60 hover:text-white'
                }`}
            >
              Talent Profile
              {view === 'profile' && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-premium-gold"
                />
              )}
            </button>
          </div>

          {/* Content Area */}
          <div id="skills-content-area" className="min-h-[600px]">
            {view === 'grid' ? (
              <div className="mb-12">
                {loading ? (
                  <div className="h-96 w-full rounded-xl bg-white/5 animate-pulse flex items-center justify-center border border-white/10">
                    <div className="text-white/40">Loading Skill Universe...</div>
                  </div>
                ) : (
                  <SkillGrid userSkills={userSkills} loading={loading} onRefresh={fetchSkills} />
                )}
              </div>
            ) : view === 'list' ? (
              <SkillManagement
                user={user as any}
                userSkills={userSkills}
                availableSkills={availableSkills}
                loading={{
                  skills: loading,
                  saving: saving,
                  assessment: false
                }}
                onRefresh={fetchSkills}
                apiClient={apiClient}
              />
            ) : (
              <div className="p-8 bg-white/5 border border-white/10 rounded-xl">
                <h2 className="text-2xl font-bold text-premium-gold mb-4">
                  Talent Profile
                </h2>
                <p className="text-white/60 mb-6">
                  Your complete talent profile will be displayed here, including discovered skills, work preferences, and capabilities.
                </p>
                <div className="grid gap-4">
                  {userSkills.length > 0 ? (
                    <div>
                      <h3 className="text-lg font-bold text-white mb-3">Your Skills ({userSkills.length})</h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {userSkills.map((skill) => (
                          <div key={skill.id} className="p-3 bg-white/5 border border-white/10 rounded-lg">
                            <div className="font-medium text-white">{skill.name}</div>
                            <div className="text-xs text-white/50 capitalize">{skill.category}</div>
                            <div className="text-xs text-premium-gold mt-1">
                              Level {skill.proficiency}/5
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <p className="text-white/40 mb-4">No skills discovered yet</p>
                      <button
                        onClick={() => setIsDiscoveryModalOpen(true)}
                        className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white font-bold rounded-lg hover:shadow-[0_0_20px_rgba(59,130,246,0.5)] transition-all"
                      >
                        Start Discovering Your Talents
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>


        </motion.div>

        <AddSkillModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onSuccess={fetchSkills}
        />

        <TalentDiscoveryModal
          isOpen={isDiscoveryModalOpen}
          onClose={() => setIsDiscoveryModalOpen(false)}
          onSuccess={fetchSkills}
        />
      </main>
    </div>
  );
};

export default Skills;